import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { LtiParams } from '@mhe/reader/models';
import { CoreApiClient } from '../core/core-api-client';
import { filter, take, switchMap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class LtiService {
  /**
   * Flag to indicate whether the API migration `?readerapi=true` query parameter was detected in the playerApiMigrationFactory.
   *
   * If the flag was detected, then an item is set in Session Storage to persist the value across page reloads.
   *
   * The flag will be false across page reloads; but if the original value persists because of Session Storage.
   */
  private readonly _apiFlagChecked$ = new BehaviorSubject<boolean | null>(null);
  public apiFlagChecked: Observable<boolean | null> = this._apiFlagChecked$.asObservable();

  setApiMigrationFlag(value: boolean): void {
    this._apiFlagChecked$.next(value);
  }

  constructor(
    private readonly coreAPIClient: CoreApiClient,
  ) { }

  get ltiParameters$(): Observable<LtiParams> {
    // some integrators set LTI params in the URL
    const directLaunchParams = this.loadDirectLaunchParams();
    if (directLaunchParams !== undefined) {
      return of(directLaunchParams);
    }

    // must wait until the playerApiMigrationFactory has run (async APP_INITIALIZER)
    return this.apiFlagChecked.pipe(
      filter(flag => flag !== null),
      take(1),
      switchMap(() => {
        // use the dynamic API client based on the session storage flag
        return this.coreAPIClient.getApiClient().fetchLTIParams();
      }),
    );
  }

  private loadDirectLaunchParams(): LtiParams | undefined {
    if (window.location.hash.lastIndexOf('direct-launch') < 0) {
      return undefined;
    }

    const queryString = window.location.hash.replace('#', '?');
    const urlParams = new URLSearchParams(queryString);
    const ltiParams: LtiParams = {};

    urlParams.forEach(function(value, key) {
      ltiParams[key] = value;
    });

    return ltiParams;
  }
}
